import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { HeaderContent } from '../classic/HeaderLayoutBase';
import { BaseLayout } from '../BaseLayout';
import { useLanguage } from '../../localization';

const useStyles = makeStyles(props => ({
  pageContainer: {
    width: '210mm',
    minHeight: '297mm',
    padding: 0,
    boxSizing: 'border-box',
    color: props?.design?.colors?.text || '#000',
    backgroundColor: props?.selectedBackground === 'blank' 
                       ? (props?.design?.colors?.background || '#ffffff') 
                       : 'transparent',
    position: 'relative',
    "& *": {
       maxWidth: "210mm",
       wordWrap: "break-word",
       overflowWrap: "break-word",
     }
  },
  topbar: {
    width: '100%',
    height: '70mm',
    minHeight: '70mm',
    maxHeight: '70mm',
    boxSizing: 'border-box',
    borderBottom: `1px solid ${props?.design?.colors?.border || '#ddd'}`,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    position: 'relative',
    padding: '0 15mm',
  },
  mainContent: {
    boxSizing: 'border-box',
    flexGrow: 1,
  },
  section: {
    marginBottom: '15px',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
  },
  sectionIcon: {
    marginRight: '8px',
    verticalAlign: 'middle',
    color: props?.design?.colors?.sidebarPrimary || props?.design?.colors?.primary || '#000',
  },
  sectionContent: {
  },
  sectionItem: {
    marginBottom: '16px',
  },
  profilePhoto: {
    width: '100px',
    height: '100px',
    border: `3px solid ${props?.design?.colors?.sidebarPrimary || props?.design?.colors?.primary || '#000000'}`,
    margin: props?.headerAlignment === 'center' ? '10px auto 15px' : '10px 0',
  },
  contactIcon: {
    color: props?.design?.colors?.sidebarPrimary || props?.design?.colors?.primary || '#000',
    fontSize: '16px',
    marginRight: '5px',
  },
}));

const TopbarLayout = ({
  formData,
  design,
  typographySettings,
  typographyStyles,
  selectedSections,
  selectedStyle,
  activeStyle,
  selectedBackground,
  activeBackgroundComponent,
  selectedSpecialEffectId,
  activeBarStyle,
  headerAlignment = 'center',
  currentPage = 0,
  totalPages = 1,
  measureMode = false,
  isPdfExport = false,
  onRenderComplete,
  ...props
}) => {
  const { t } = useLanguage();

  // Log the design colors received
  console.log('TopbarLayout received design.colors:', design?.colors);

  const classes = useStyles({ design, typographyStyles, selectedBackground });

  const innerAlignItems = headerAlignment === 'left' ? 'flex-start' : headerAlignment === 'right' ? 'flex-end' : 'center';

  React.useEffect(() => {
    if (typeof onRenderComplete === 'function') {
      onRenderComplete();
    }
  }, [onRenderComplete]);

  return (
    <Box 
      className={classes.pageContainer}
      sx={{ 
        ...typographyStyles,
        ...(measureMode ? { position: 'absolute', visibility: 'hidden' } : {}),
        ...(isPdfExport ? { position: 'relative', visibility: 'visible' } : {}),
      }}
    >
      {currentPage === 0 && (
        <Box 
          className={`${classes.topbar} topbar`}
          sx={{
            alignItems: innerAlignItems,
            backgroundColor: design?.colors?.sidebarBackground || '#f5f5f5',
            textAlign: headerAlignment,
            '& > *': {
              textAlign: headerAlignment,
            },
            ...typographyStyles,
          }}
        >
          <HeaderContent
            formData={formData}
            design={design}
            activeStyle={activeStyle}
            headerAlignment={headerAlignment}
          />
        </Box>
      )}

      <Box 
        className={classes.mainContent}
        sx={{
          padding: currentPage === 0 
            ? '8mm 15mm 15mm 15mm'  // On first page with topbar
            : '15mm',               // On subsequent pages, align with other layouts
        }}
      >
        {selectedSections.map((section) => {
          switch(section.name) {
            case 'Professional Summary':
              return BaseLayout.renderProfessionalSummary(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Work Experience':
              return BaseLayout.renderWorkExperiences(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Education':
              return BaseLayout.renderEducations(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Skills': 
              return BaseLayout.renderSkills(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
            case 'Projects': 
              return BaseLayout.renderProjects(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Certifications': 
              return BaseLayout.renderCertifications(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Awards and Honors':
              return BaseLayout.renderAwardsAndHonors(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Languages':
              return BaseLayout.renderLanguages(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
            case 'Volunteer Experience':
              return BaseLayout.renderVolunteerExperiences(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'Publications':
              return BaseLayout.renderPublications(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            case 'References':
              return BaseLayout.renderReferences(formData, classes, design?.colors, activeStyle, 'topbar', selectedBackground, selectedSpecialEffectId, t);
            default:
               console.warn(`Render function not explicitly mapped for section: ${section.name}`);
              return null;
          }
        })}
      </Box>

      {totalPages > 1 && !measureMode && (
        <Typography
          className="smallText"
          sx={{
            position: 'absolute',
            bottom: '10mm',
            right: '10mm',
            color: design?.colors?.textSecondary || '#666',
          }}
        >
          Page {currentPage + 1} of {totalPages}
        </Typography>
      )}
    </Box>
  );
};

export default TopbarLayout;
